import Phaser from 'phaser';
import { GAME_HEIGHT, GAME_WIDTH } from '../data/gameConstants';
import { ResponsiveSystem } from '../systems/ResponsiveSystem';
import { UIStyle } from './UIStyle';

/** Ruang di dalam panel (tab speaker overlap di luar area ini). */
const PANEL_TOP_INSET = 20;
const PANEL_BOTTOM_INSET = 14;
const FOOTER_HINT_H = 28;
const TAB_OVERLAP = 10;

export interface DialogueTextLayout {
  wrapWidth: number;
  maxTextHeight: number;
  bodyStyle: Phaser.Types.GameObjects.Text.TextStyle;
}

export class DialogueBox {
  private scene: Phaser.Scene;
  private container?: Phaser.GameObjects.Container;
  private onAdvance?: () => void;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  getTextLayout(): DialogueTextLayout {
    const isMobile = ResponsiveSystem.isMobile(this.scene);
    const safe = ResponsiveSystem.getSafeBounds(this.scene.game, {
      left: 16,
      right: 16,
      bottom: isMobile ? 24 : 20,
      top: isMobile ? 72 : 64,
    });

    const paddingX = isMobile ? 20 : 24;
    const boxWidth = Math.min(GAME_WIDTH - 32, safe.width + 24);
    const wrapWidth = boxWidth - paddingX * 2;
    const minH = isMobile ? 120 : 116;
    const maxPanelHeight = Math.min(
      isMobile ? 340 : 320,
      Math.max(minH, safe.bottom - safe.top - (isMobile ? 24 : 16)),
    );
    const maxTextHeight = Math.max(
      48,
      maxPanelHeight - PANEL_TOP_INSET - FOOTER_HINT_H - PANEL_BOTTOM_INSET,
    );
    const bodyStyle: Phaser.Types.GameObjects.Text.TextStyle = {
      ...UIStyle.panelBody(isMobile ? UIStyle.px(17) : UIStyle.px(19)),
      wordWrap: { width: wrapWidth, useAdvancedWrap: true },
      align: 'left',
      lineSpacing: 8,
      padding: { x: 2, y: 6 },
    };
    return { wrapWidth, maxTextHeight, bodyStyle };
  }

  textFits(text: string, layout = this.getTextLayout()): boolean {
    const probe = this.scene.add.text(0, 0, text, layout.bodyStyle);
    const height = probe.height;
    probe.destroy();
    return height <= layout.maxTextHeight + 1;
  }

  show(speaker: string, text: string, onAdvance: () => void): void {
    this.hide();
    this.onAdvance = onAdvance;

    const isMobile = ResponsiveSystem.isMobile(this.scene);
    const safe = ResponsiveSystem.getSafeBounds(this.scene.game, {
      left: 16,
      right: 16,
      bottom: isMobile ? 24 : 20,
      top: isMobile ? 72 : 64,
    });

    const paddingX = isMobile ? 20 : 24;
    const boxWidth = Math.min(GAME_WIDTH - 32, safe.width + 24);
    const layout = this.getTextLayout();
    const bodyStyle = layout.bodyStyle;

    const probe = this.scene.add.text(0, 0, text, bodyStyle);
    const textH = probe.height;
    probe.destroy();

    const minH = isMobile ? 120 : 116;
    const maxPanelHeight = Math.min(
      isMobile ? 340 : 320,
      Math.max(minH, safe.bottom - safe.top - (isMobile ? 24 : 16)),
    );
    const contentH = PANEL_TOP_INSET + textH + FOOTER_HINT_H + PANEL_BOTTOM_INSET;
    const boxHeight = Phaser.Math.Clamp(contentH, minH, maxPanelHeight);

    const panelX = safe.centerX;
    const panelY = safe.bottom - boxHeight / 2 - (isMobile ? 6 : 8);
    const panelLeft = panelX - boxWidth / 2;
    const panelTop = panelY - boxHeight / 2;
    const bodyY = panelTop + PANEL_TOP_INSET;

    this.container = this.scene.add.container(0, 0);

    const overlay = UIStyle.createDarkOverlay(this.scene, GAME_WIDTH, GAME_HEIGHT, 0.58);
    overlay.setInteractive();

    const bg = UIStyle.createDialoguePanel(this.scene, panelX, panelY, boxWidth, boxHeight);

    const speakerStyle = UIStyle.panelTitle(isMobile ? UIStyle.px(18) : UIStyle.px(20));
    const tabPadX = isMobile ? 16 : 20;
    const maxTabW = boxWidth - paddingX * 2;
    const minTabW = isMobile ? 96 : 108;

    const speakerProbe = this.scene.add.text(0, 0, speaker, speakerStyle);
    let tabW = Math.max(minTabW, speakerProbe.width + tabPadX * 2);
    let tabH = isMobile ? 34 : 36;

    const speakerTextStyle: Phaser.Types.GameObjects.Text.TextStyle = { ...speakerStyle };
    if (tabW > maxTabW) {
      tabW = maxTabW;
      speakerTextStyle.wordWrap = { width: tabW - tabPadX * 2 };
      const wrapProbe = this.scene.add.text(0, 0, speaker, speakerTextStyle);
      tabH = Math.max(tabH, wrapProbe.height + tabPadX);
      wrapProbe.destroy();
    }
    speakerProbe.destroy();

    const tabX = panelLeft + paddingX;
    const tabY = panelTop - tabH + TAB_OVERLAP;
    const speakerTab = UIStyle.createSpeakerTab(this.scene, tabX, tabY, tabW, tabH);

    const speakerText = this.scene.add.text(
      tabX + tabW / 2,
      tabY + tabH / 2,
      speaker,
      speakerTextStyle,
    );
    speakerText.setOrigin(0.5);

    const bodyText = this.scene.add.text(
      panelLeft + paddingX,
      bodyY,
      text,
      bodyStyle,
    );
    bodyText.setOrigin(0, 0);

    const hintY = panelTop + boxHeight - PANEL_BOTTOM_INSET;
    const hint = this.scene.add.text(
      panelLeft + boxWidth - paddingX,
      hintY,
      isMobile ? 'Ketuk untuk lanjut ▶' : 'E / Ketuk untuk lanjut ▶',
      UIStyle.panelMuted(isMobile ? UIStyle.px(14) : UIStyle.px(15)),
    );
    hint.setOrigin(1, 1);

    this.container.add([overlay, bg, speakerTab, speakerText, bodyText, hint]);
    this.container.setDepth(1000);
    this.container.setScrollFactor(0);
    overlay.setScrollFactor(0);

    overlay.on('pointerup', () => this.advance());
  }

  private advance(): void {
    this.onAdvance?.();
  }

  hide(): void {
    this.container?.destroy();
    this.container = undefined;
  }

  destroy(): void {
    this.hide();
  }
}
